import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import FileUpload from './FileUpload';
import FilePreviewModal from './FilePreviewModal';
import AddCustomDocumentModal from './AddCustomDocumentModal';
import type { DocumentChecklist } from '../../types';
import trackingService from '../../services/tracking.service';

interface DocumentListProps {
    checklist: DocumentChecklist;
    onUpdate: () => void;
    applicationStatus?: string;
}

export default function DocumentList({ checklist, onUpdate, applicationStatus }: DocumentListProps) {
    const { language } = useI18n();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<{ url: string; name: string; type: string } | null>(null);
    const [expandedDoc, setExpandedDoc] = useState<number | null>(null);
    const [uploading, setUploading] = useState<number | null>(null);
    const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);

    // Payment confirmation status
    const isPaymentConfirmed = applicationStatus
        ? !['pending_payment', 'pending_confirmation', 'rejected', 'cancelled'].includes(applicationStatus)
        : true;
    const isAwaitingConfirmation = applicationStatus === 'pending_confirmation';

    const handleUploadComplete = async (documentId: number, fileData: any) => {
        if (!isPaymentConfirmed) {
            alert(
                language === 'ar'
                    ? 'لا يمكن رفع المستندات حالياً. يرجى الانتظار حتى يقوم المشرف بتأكيد الدفع أولاً.'
                    : 'Cannot upload documents. Please wait until admin confirms your payment.'
            );
            return;
        }

        try {
            setUploading(documentId);
            await trackingService.uploadDocument(checklist.application_id, documentId, {
                document_type: fileData.category,
                file_url: fileData.public_url,
                file_name: fileData.file_name,
                file_size: fileData.file_size,
                file_type: fileData.mime_type
            });
            onUpdate();
            setExpandedDoc(null);
        } catch (error: any) {
            console.error('Failed to link document:', error);
            const detailMsg = error?.response?.data?.detail;
            alert(detailMsg || (language === 'ar' ? 'حدث خطأ أثناء حفظ الملف' : 'Error saving file'));
        } finally {
            setUploading(null);
        }
    };

    const handleAddCustomDocument = async (data: {
        document_type: string;
        document_name: string;
        file_url: string;
        file_name: string;
        file_size: number;
        file_type: string;
        notes?: string;
    }) => {
        if (!isPaymentConfirmed) {
            alert(
                language === 'ar'
                    ? 'لا يمكن رفع المستندات حالياً. يرجى الانتظار حتى يقوم المشرف بتأكيد الدفع أولاً.'
                    : 'Cannot upload documents. Please wait until admin confirms your payment.'
            );
            return;
        }

        await trackingService.addCustomDocument(checklist.application_id, data);
        onUpdate();
    };

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'approved':
                return {
                    badge: 'bg-gradient-to-r from-green-500 to-emerald-500 text-white',
                    icon: (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                    ),
                    label: language === 'ar' ? 'مُعتمد' : 'Approved',
                    cardBg: 'bg-gradient-to-br from-green-50 to-emerald-50/50 border-green-200/50',
                    iconBg: 'bg-gradient-to-br from-green-500 to-emerald-500 text-white'
                };
            case 'rejected':
                return {
                    badge: 'bg-gradient-to-r from-red-500 to-rose-500 text-white',
                    icon: (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ),
                    label: language === 'ar' ? 'مرفوض' : 'Rejected',
                    cardBg: 'bg-gradient-to-br from-red-50 to-rose-50/50 border-red-200/50',
                    iconBg: 'bg-gradient-to-br from-red-500 to-rose-500 text-white'
                };
            case 'uploaded':
                return {
                    badge: 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white',
                    icon: (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    ),
                    label: language === 'ar' ? 'قيد المراجعة' : 'Reviewing',
                    cardBg: 'bg-gradient-to-br from-blue-50 to-indigo-50/50 border-blue-200/50',
                    iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white'
                };
            default:
                return {
                    badge: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white',
                    icon: (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                    ),
                    label: language === 'ar' ? 'مطلوب رفعه' : 'Required',
                    cardBg: 'bg-gradient-to-br from-amber-50 to-orange-50/50 border-amber-200/50',
                    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white'
                };
        }
    };

    const getDocumentIcon = (docType: string) => {
        const type = docType.toLowerCase();
        if (type.includes('passport') || type.includes('id')) {
            return (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                </svg>
            );
        }
        if (type.includes('transcript') || type.includes('grade') || type.includes('certificate')) {
            return (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            );
        }
        if (type.includes('photo') || type.includes('image') || type.includes('picture')) {
            return (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            );
        }
        if (type.includes('language') || type.includes('ielts') || type.includes('toefl')) {
            return (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
            );
        }
        return (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
        );
    };

    // Group documents by status for better organization
    const pendingDocs = checklist.documents.filter(d => d.status === 'required' || d.status === 'pending');
    const uploadedDocs = checklist.documents.filter(d => d.status === 'uploaded');
    const rejectedDocs = checklist.documents.filter(d => d.status === 'rejected');
    const approvedDocs = checklist.documents.filter(d => d.status === 'approved');

    // Catch any documents with unknown status
    const knownStatuses = ['required', 'pending', 'uploaded', 'rejected', 'approved'];
    const otherDocs = checklist.documents.filter(d => !knownStatuses.includes(d.status));

    // Sort: rejected first, then pending (need action), then other, then uploaded (under review), then approved
    const sortedDocuments = [...rejectedDocs, ...pendingDocs, ...otherDocs, ...uploadedDocs, ...approvedDocs];

    // Calculate stats
    const needsAction = rejectedDocs.length + pendingDocs.length;
    const progressPercent = checklist.total_documents > 0
        ? Math.round((checklist.approved_count / checklist.total_documents) * 100)
        : 0;

    return (
        <div className="bg-white rounded-3xl border border-gray-100 p-5 sm:p-6 shadow-sm h-full flex flex-col">
            {/* Header */}
            <div className="flex items-start sm:items-center justify-between gap-4 mb-5 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#5B3FD0]/10 to-[#5B3FD0]/5 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="font-bold text-[#2C2C2C]">
                            {language === 'ar' ? 'المستندات' : 'Documents'}
                        </h3>
                        <p className="text-xs text-[#707070]">
                            {language === 'ar' ? `${checklist.approved_count} من ${checklist.total_documents} مُعتمد` : `${checklist.approved_count} of ${checklist.total_documents} approved`}
                        </p>
                    </div>
                </div>

                {/* Mini Progress & Add Button */}
                <div className="flex items-center gap-3">
                    {needsAction > 0 && isPaymentConfirmed && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 rounded-lg text-xs font-bold animate-pulse">
                            {needsAction} {language === 'ar' ? 'مطلوب' : 'needed'}
                        </span>
                    )}
                    <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#5B3FD0] to-[#7B5FE0] rounded-full transition-all duration-500"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <span className="text-xs font-bold text-[#5B3FD0]">{progressPercent}%</span>
                    </div>
                </div>
            </div>

            {/* Payment Not Confirmed Warning Alert Banner */}
            {!isPaymentConfirmed && (
                <div className={`mb-4 p-4 rounded-2xl border flex items-start gap-3.5 flex-shrink-0 shadow-sm ${
                    isAwaitingConfirmation
                        ? 'bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/5 border-amber-300/80'
                        : 'bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-purple-500/5 border-purple-300/80'
                }`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
                        isAwaitingConfirmation
                            ? 'bg-amber-500 text-white shadow-amber-500/20'
                            : 'bg-[#5B3FD0] text-white shadow-[#5B3FD0]/20'
                    }`}>
                        {isAwaitingConfirmation ? (
                            <svg className="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-bold mb-1 ${isAwaitingConfirmation ? 'text-amber-900' : 'text-purple-900'}`}>
                            {isAwaitingConfirmation
                                ? (language === 'ar' ? 'في انتظار تأكيد الدفع من الإدارة' : 'Awaiting Admin Payment Confirmation')
                                : (language === 'ar' ? 'يلزم إتمام الدفع وتأكيده أولاً' : 'Payment Confirmation Required')}
                        </h4>
                        <p className={`text-xs leading-relaxed ${isAwaitingConfirmation ? 'text-amber-800' : 'text-purple-800'}`}>
                            {isAwaitingConfirmation
                                ? (language === 'ar'
                                    ? 'تم إرسال إيصال الدفع وهو قيد المراجعة حالياً من قبل الإدارة. سيتم تفعيل إمكانية رفع وتعديل المستندات تلقائياً فور تأكيد الدفع.'
                                    : 'Your payment receipt has been submitted and is currently being reviewed by the admin. Document upload will be enabled automatically once payment is confirmed.')
                                : (language === 'ar'
                                    ? 'يرجى سداد رسوم الطلب والانتظار حتى تأكيد الدفع من قبل الإدارة لتتمكن من رفع المستندات المطلوبة.'
                                    : 'Please complete your application payment and wait for admin confirmation to begin uploading required documents.')}
                        </p>
                    </div>
                </div>
            )}

            {/* Add Document Button */}
            <button
                onClick={() => {
                    if (!isPaymentConfirmed) {
                        alert(
                            isAwaitingConfirmation
                                ? (language === 'ar'
                                    ? 'رفع المستندات مغلق حالياً. يرجى الانتظار حتى يؤكد المشرف الدفع.'
                                    : 'Document upload is locked. Please wait until admin confirms your payment.')
                                : (language === 'ar'
                                    ? 'يلزم سداد وتأكيد الدفع أولاً لتتمكن من إضافة مستندات جديدة.'
                                    : 'Payment confirmation is required before adding new documents.')
                        );
                        return;
                    }
                    setIsAddDocumentModalOpen(true);
                }}
                disabled={!isPaymentConfirmed}
                className={`mb-4 w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl transition-all ${
                    !isPaymentConfirmed
                        ? 'bg-gray-100 text-gray-400 border-2 border-gray-200 cursor-not-allowed shadow-none'
                        : 'bg-gradient-to-r from-[#5B3FD0] to-[#7B5FE0] hover:from-[#4B32B3] hover:to-[#6B4FD0] text-white shadow-lg shadow-[#5B3FD0]/30 hover:shadow-xl hover:shadow-[#5B3FD0]/40 hover:-translate-y-0.5 border-2 border-[#5B3FD0]/50'
                }`}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {!isPaymentConfirmed ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    )}
                </svg>
                <span>
                    {!isPaymentConfirmed
                        ? (language === 'ar' ? 'رفع المستندات مغلق (في انتظار تأكيد الدفع)' : 'Document Upload Locked (Pending Payment Confirmation)')
                        : (language === 'ar' ? 'إضافة مستند جديد' : 'Add New Document')}
                </span>
            </button>

            {/* Action Required Banner (Only when payment is confirmed) */}
            {isPaymentConfirmed && needsAction > 0 && (
                <div className={`mb-4 p-3 rounded-xl flex items-center gap-3 flex-shrink-0 ${
                    rejectedDocs.length > 0
                        ? 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-100'
                        : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100'
                }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        rejectedDocs.length > 0 ? 'bg-red-500' : 'bg-amber-500'
                    }`}>
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold ${rejectedDocs.length > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                            {rejectedDocs.length > 0
                                ? (language === 'ar' ? `${rejectedDocs.length} مستند مرفوض - يحتاج إعادة رفع` : `${rejectedDocs.length} document(s) rejected - needs re-upload`)
                                : (language === 'ar' ? `${pendingDocs.length} مستند في انتظار الرفع` : `${pendingDocs.length} document(s) waiting for upload`)
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Document List */}
            <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-3 custom-scrollbar">
                {sortedDocuments.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center p-6 text-center">
                        <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl flex items-center justify-center mb-3">
                            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <p className="text-sm text-[#707070]">
                            {language === 'ar' ? 'لا توجد مستندات مطلوبة حالياً' : 'No documents required'}
                        </p>
                    </div>
                ) : (
                    sortedDocuments.map((doc, index) => {
                        const config = getStatusConfig(doc.status);
                        const isExpanded = expandedDoc === doc.id;
                        const isUploading = uploading === doc.id;

                        return (
                            <div
                                key={doc.id}
                                className={`rounded-2xl border transition-all duration-200 hover:shadow-md ${config.cardBg} ${isExpanded ? 'ring-2 ring-[#5B3FD0]/20' : ''}`}
                                style={{ animationDelay: `${index * 30}ms` }}
                            >
                                {/* Main Row */}
                                <div
                                    className="p-3 sm:p-4 cursor-pointer"
                                    onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
                                >
                                    <div className="flex items-center gap-3">
                                        {/* Status Icon */}
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${config.iconBg}`}>
                                            {getDocumentIcon(doc.document_type)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h4 className="font-bold text-[#2C2C2C] text-sm">
                                                    {language === 'ar' ? doc.document_name_ar : doc.document_name}
                                                </h4>
                                                {doc.is_mandatory && (
                                                    <span className="text-red-500 text-xs font-bold">*</span>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold shadow-sm ${config.badge}`}>
                                                    {config.icon}
                                                    {config.label}
                                                </span>

                                                {doc.is_consultant_requested && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-600">
                                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                        {language === 'ar' ? 'طلب مستشار' : 'Consultant'}
                                                    </span>
                                                )}

                                                {doc.file_url && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFile({
                                                                url: doc.file_url!,
                                                                name: doc.file_name || 'document',
                                                                type: doc.file_type || ''
                                                            });
                                                            setIsPreviewOpen(true);
                                                        }}
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#5B3FD0] to-[#7B5FE0] hover:from-[#4B32B3] hover:to-[#6B4FD0] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#5B3FD0]/30 hover:shadow-lg hover:shadow-[#5B3FD0]/40 hover:-translate-y-0.5"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                        {language === 'ar' ? 'معاينة' : 'Preview'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Quick Actions */}
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {(doc.status === 'pending' || doc.status === 'rejected') && !isExpanded && (
                                                !isPaymentConfirmed ? (
                                                    <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-100/90 text-gray-500 border border-gray-200 rounded-xl text-xs font-medium cursor-not-allowed">
                                                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                        </svg>
                                                        <span className="hidden xs:inline">
                                                            {language === 'ar' ? 'مغلق حتى تأكيد الدفع' : 'Locked'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedDoc(doc.id);
                                                        }}
                                                        className="flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-r from-[#5B3FD0] to-[#7B5FE0] hover:from-[#4B32B3] hover:to-[#6B4FD0] text-white text-xs sm:text-sm font-bold rounded-xl transition-all shadow-lg shadow-[#5B3FD0]/40 hover:shadow-xl hover:shadow-[#5B3FD0]/50 hover:-translate-y-0.5 ring-2 ring-[#5B3FD0]/50 ring-offset-2 ring-offset-white"
                                                    >
                                                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                        </svg>
                                                        <span className="hidden xs:inline">
                                                            {doc.status === 'rejected'
                                                                ? (language === 'ar' ? 'إعادة رفع' : 'Re-upload')
                                                                : (language === 'ar' ? 'رفع الملف' : 'Upload')
                                                            }
                                                        </span>
                                                    </button>
                                                )
                                            )}

                                            <button className="p-1.5 hover:bg-white/50 rounded-lg transition-colors">
                                                <svg
                                                    className={`w-4 h-4 text-[#707070] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div className="px-3 sm:px-4 pb-4 border-t border-gray-100/50 pt-3 animate-fadeIn">
                                        {/* Request Reason */}
                                        {doc.request_reason && (
                                            <div className="mb-3 p-2.5 bg-purple-50 rounded-xl flex items-start gap-2">
                                                <svg className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                                </svg>
                                                <p className="text-xs text-purple-700 italic">"{doc.request_reason}"</p>
                                            </div>
                                        )}

                                        {/* Rejection Reason */}
                                        {doc.rejection_reason && (
                                            <div className="mb-3 p-2.5 bg-red-50 rounded-xl flex items-start gap-2">
                                                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <div>
                                                    <p className="text-xs font-bold text-red-700">
                                                        {language === 'ar' ? 'سبب الرفض:' : 'Rejection reason:'}
                                                    </p>
                                                    <p className="text-xs text-red-600 mt-0.5">{doc.rejection_reason}</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Upload Section */}
                                        {doc.status !== 'approved' && (
                                            <div className="bg-white rounded-xl p-3 border border-gray-100 relative">
                                                {!isPaymentConfirmed ? (
                                                    <div className="p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-center">
                                                        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                                                        </div>
                                                        <p className="text-xs font-bold text-gray-700 mb-1">
                                                            {language === 'ar' ? 'رفع هذا المستند مغلق حالياً' : 'Document Upload Locked'}
                                                        </p>
                                                        <p className="text-xs text-gray-500 max-w-sm mx-auto">
                                                            {isAwaitingConfirmation
                                                                ? (language === 'ar'
                                                                    ? 'يرجى الانتظار حتى يقوم المشرف بتأكيد الدفع لتفعيل إمكانية رفع هذا المستند.'
                                                                    : 'Please wait until admin confirms your payment to enable uploading this document.')
                                                                : (language === 'ar'
                                                                    ? 'يلزم سداد رسوم الطلب وتأكيدها لتفعيل إمكانية رفع هذا المستند.'
                                                                    : 'Payment confirmation is required before uploading this document.')
                                                            }
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {isUploading && (
                                                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                                                                <div className="flex items-center gap-2">
                                                                    <svg className="w-5 h-5 text-[#5B3FD0] animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                                    </svg>
                                                                    <span className="text-sm font-medium text-[#5B3FD0]">
                                                                        {language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <p className="text-xs font-semibold text-[#2C2C2C] mb-2">
                                                            {doc.status === 'rejected'
                                                                ? (language === 'ar' ? 'إعادة رفع الملف' : 'Re-upload file')
                                                                : (language === 'ar' ? 'رفع الملف' : 'Upload file')
                                                            }
                                                        </p>
                                                        <FileUpload
                                                            onUploadComplete={(fileData) => handleUploadComplete(doc.id, fileData)}
                                                            category={doc.document_type}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Upload Info */}
                                        {doc.uploaded_at && (
                                            <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-[#707070]">
                                                <span className="flex items-center gap-1">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {new Date(doc.uploaded_at).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
                                                </span>
                                                {doc.uploaded_by_name && (
                                                    <span className="flex items-center gap-1">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                        {doc.uploaded_by_name}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Completion Message */}
            {checklist.is_complete && (
                <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-xl flex items-center gap-3 flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <div>
                        <p className="font-bold text-green-800 text-sm">
                            {language === 'ar' ? 'اكتملت جميع المستندات!' : 'All documents complete!'}
                        </p>
                        <p className="text-xs text-green-600">
                            {language === 'ar' ? 'تم اعتماد جميع المستندات بنجاح' : 'All documents have been approved'}
                        </p>
                    </div>
                </div>
            )}

            {/* File Preview Modal */}
            {selectedFile && (
                <FilePreviewModal
                    isOpen={isPreviewOpen}
                    onClose={() => {
                        setIsPreviewOpen(false);
                        setSelectedFile(null);
                    }}
                    fileUrl={selectedFile.url}
                    fileName={selectedFile.name}
                    fileType={selectedFile.type}
                />
            )}

            {/* Add Custom Document Modal */}
            <AddCustomDocumentModal
                isOpen={isAddDocumentModalOpen}
                onClose={() => setIsAddDocumentModalOpen(false)}
                onSubmit={handleAddCustomDocument}
            />
        </div>
    );
}
